/**
 * The autopilot (M14a) — the centerline demoted from choreographer to
 * WISHDIR. One general control law replaces the assist law's special
 * cases (climb carry, hands-off threshold, curvature term):
 *
 * - Pure pursuit: chase a lookahead point on the rail at
 *   L = clamp(lookGain·|v|, L_MIN, L_MAX) → wishdir (Coulter '92; for a
 *   marble the controller degenerates to "seek the point").
 * - Speed planning: per-station corner speeds from curvature split into
 *   yaw (wall-launch risk) and CONVEX pitch (crest-launch risk) — concave
 *   valleys don't brake, so chute dives stay dramatic — swept backward
 *   online: allowed(k) = sqrt(vc_k² + 2·A_BRAKE·Δs). wishspeed is the
 *   minimum of the local target and that envelope.
 * - Accelerate with the Quake/Marble Blast projection contract:
 *   deficit = wishspeed − dot(v, wishdir); deficit ≤ 0 → NO force
 *   (momentum is sacred — a downhill marble is never fought; this is what
 *   replaces the hands-off threshold). Positive deficit → a saturating
 *   authority a = min(KP·deficit, A_TRACTION)·grip; climbs emerge (the
 *   marble slows, the deficit grows, authority rises above along-slope
 *   gravity) instead of being scripted by a gravity-cancelling carry.
 * - Decisive braking only above the swept envelope (separate clamp, like
 *   MBHaxe's brakingAcceleration vs angularAcceleration).
 * - Airborne (rail floor left behind): weak linear air control toward the
 *   lookahead — enough to line up a landing, never enough to fly (MBHaxe
 *   airAccel 5 vs g 20).
 *
 * Zone semantics (flight pricing, gravity tilt, restitution, centering
 * springs, descent pulls) stay shared with rail.ts — the autopilot only
 * replaces the propulsion/braking half of stationForce.
 */
import { type Rail, RAIL_STEP, type RailMod } from "./rail.ts";
import { GRAVITY_Y } from "./engine.ts";
import { rngFamily } from "../../gen/rng.ts";

/** Per-marble controller personality (M14c). The defaults are the
 * neutral baseline; pilotFor() draws seed-stable jitter around them. */
export interface Pilot {
  /** Corner-speed margin: multiplies the curvature-derived corner speed
   * (not the target floor) — >1 corners braver, <1 more cautious. */
  bravery: number;
  /** Pursuit lookahead gain k in L = clamp(k·|v|, L_MIN, L_MAX). */
  lookGain: number;
  /** Jump-eagerness: multiplies the max top-up the pilot will attempt. */
  eagerness: number;
  /** Boost aggression: scales the attack/defend trigger ranges — when
   * this pilot chooses to SPEND fuel (drawn last in pilotFor, so adding
   * it never re-rolled the older traits). */
  aggro: number;
  /** Recharge-rate multiplier: identical rates locked every tank into
   * the same cycle and the pack surged in unison — regen spread turns
   * the waves into duels. */
  regen: number;
  /** Boost speed increase: this pilot's wishspeed multiplier while
   * burning (the envelope still caps it — corner-safe at any value). */
  boostMult: number;
  /** Boost fuel efficiency: divides the burn rate (1.25 = the tank
   * lasts 25% longer at the same speed). */
  efficiency: number;
  /** Lateral offset target from the rail line (world units; drivers set
   * this per step from pilotOffset() — 0 rides the line). */
  offset: number;
  /** Wander parameters (closed-form line-offset noise): amplitude and
   * two seed-drawn phases. Amp 0 = no wander. */
  wanderAmp: number;
  phase1: number;
  phase2: number;
  /** Wake-rider appetite (M20a): scales the draft bonus. */
  wake: number;
  /** Braid alt-lane threshold (M20a): braidLaneAlt compares its single
   * draw against this — lane identity is CHARACTER, so it reads the
   * base pilot, never the rubber-banded scratch. */
  lanePref: number;
  /** Overtake chirality (M20a): pass-left (−1) or pass-right (+1). */
  chir: number;
  /** Cornering style (M20b): scales the PLANNING decel in the brake
   * sweep. >1 = late-braker (dive-bombs entries), <1 = early-coaster.
   * Hard cap 1.4: planned decel 6·1.4 = 8.4 stays under A_BRAKE_MAX 10,
   * so a late-braker can always shed the speed it planned to — envelope
   * violations impossible by construction. */
  brakeStyle: number;
  /** Post-contact temper (M20b): 0-1 scales the aggression window a
   * shove opens (temperStep). */
  temper: number;
}

export const DEFAULT_PILOT: Pilot = {
  bravery: 1,
  lookGain: 0.55,
  eagerness: 1,
  aggro: 1,
  regen: 1,
  boostMult: 1.55,
  efficiency: 1,
  offset: 0,
  wanderAmp: 0,
  phase1: 0,
  phase2: 0,
  wake: 1,
  lanePref: 0.5,
  chir: 1,
  brakeStyle: 1,
  temper: 0,
};

/** Seed-stable personality for marble `index` (label-derived stream —
 * adding marbles never perturbs earlier ones; new traits draw AFTER all
 * older ones so existing personalities stay byte-identical). */
export function pilotFor(seed: string, index = 0): Pilot {
  const rng = rngFamily(seed, `pilot/${index}`);
  const bravery = 0.92 + rng.float() * 0.18;
  const lookGain = 0.48 + rng.float() * 0.16;
  const eagerness = 0.9 + rng.float() * 0.2;
  const wanderAmp = 0.1 + rng.float() * 0.1;
  const phase1 = rng.float() * Math.PI * 2;
  const phase2 = rng.float() * Math.PI * 2;
  const aggro = 0.7 + rng.float() * 0.6; // appended draw (boost round)
  const regen = 0.75 + rng.float() * 0.5; // appended draw (boost round)
  const boostMult = 1.35 + rng.float() * 0.4; // appended draw
  const efficiency = 0.8 + rng.float() * 0.45; // appended draw
  const wake = 0.6 + rng.float() * 0.8; // appended draw (M20a)
  const lanePref = 0.35 + rng.float() * 0.3; // appended draw (M20a)
  const chir = rng.float() < 0.5 ? -1 : 1; // appended draw (M20a)
  const brakeStyle = 0.8 + rng.float() * 0.55; // appended draw (M20b)
  const temper = rng.float(); // appended draw (M20b)
  return {
    bravery,
    lookGain,
    eagerness,
    aggro,
    regen,
    boostMult,
    efficiency,
    offset: 0,
    wanderAmp,
    phase1,
    phase2,
    wake,
    lanePref,
    chir,
    brakeStyle,
    temper,
  };
}

// --- post-shove temper (M20b) ----------------------------------------------
//
// The shim has no contact events, so "shoved" is the deterministic proxy
// the pack already computes: a peer inside contact distance during the
// decision's neighbor scan. A contact opens a ~3s window during which
// the pilot's aggression (boost triggers) and shove-through bravery run
// hot, scaled by the temper trait. Pure state law, boostStep's shape —
// drivers hold the clock per racer.

/** Aggression window a contact opens (seconds). */
export const TEMPER_WINDOW = 3;

/** One fixed-step of the temper clock: `contactNear` = a peer within
 * contact distance this decision. Returns the remaining hot-window time
 * (0 = cool). Re-contact re-arms the full window (no accumulation). */
export function temperStep(
  clock: number,
  dt: number,
  contactNear: boolean,
): number {
  if (contactNear) return TEMPER_WINDOW;
  return Math.max(0, clock - dt);
}

/** Effective aggro/bravery multipliers while the temper window runs. */
export function temperHeat(
  clock: number,
  temper: number,
): { aggro: number; bravery: number } {
  if (clock <= 0 || temper <= 0) return { aggro: 1, bravery: 1 };
  return { aggro: 1 + 0.5 * temper, bravery: 1 + 0.08 * temper };
}

/** Braid lane choice (M17): true = the alt lane. Each racer draws from
 * ITS OWN label-derived stream, `lane/${idx}/${segIndex}` — the project's
 * rng discipline: no sequential draws, no draw-order coupling, seed-
 * stable, and every racer's stream is independent exactly like pilotFor
 * personalities (changing one racer's inputs can never affect another's
 * choice). Deterministic per (seed, racer, braid); over a pack it splits
 * roughly in half, so the stream visibly forks and rejoins. */
export function braidLaneAlt(
  seed: string,
  idx: number,
  segIndex: number,
  /** Personality threshold (M20a): SAME label, SAME single draw — only
   * the comparison moves, so racer streams stay untouched and lane
   * splits lean with character instead of a fixed coin. */
  pref = 0.5,
): boolean {
  return rngFamily(seed, `lane/${idx}/${segIndex}`).float() < pref;
}

// --- drafting (M20a) --------------------------------------------------------
//
// A trailing marble rides the leader's wake: a wishspeed multiplier in a
// window behind the same-lane racer ahead, applied through the SAME
// envelope law as boost (wishspeed = min(target·mult, vSafe)) — drafting
// into a corner brakes identically, so the slingshot can only happen
// where the track permits it. Riders fake the same law kinematically.

/** Wake window: closer than this, contact/separation owns the pair. */
export const DRAFT_MIN = 1.2;
/** Wake dissipates by this progress gap (world units). */
export const DRAFT_MAX = 5.0;
/** Peak bonus at DRAFT_MIN for wake = 1 (a ~10% slingshot). */
export const DRAFT_GAIN = 0.1;

/** Pure draft law: target-speed multiplier for a racer `gapAhead` behind
 * its same-lane leader. 1 outside the window; linear falloff inside. */
export function draftFactor(gapAhead: number | null, wake: number): number {
  if (gapAhead === null || gapAhead < DRAFT_MIN || gapAhead >= DRAFT_MAX) {
    return 1;
  }
  const falloff = (DRAFT_MAX - gapAhead) / (DRAFT_MAX - DRAFT_MIN);
  return 1 + DRAFT_GAIN * wake * falloff;
}

// --- the boost economy (competition round) ---------------------------------
//
// Racing on autopilot needs a resource to SPEND or every outcome is
// contact luck: each pilot gets a fuel tank that burns in ~3s of boost
// and recharges in ~12s of restraint. Ignition is a decision — attack a
// rival ahead, break away from a chaser, or bleed an overflowing tank —
// gated on envelope HEADROOM (wishspeed stays min(target·mult, vSafe),
// so a boosting marble still brakes for every corner: safety is
// unchanged by construction; boost only buys speed where the track
// permits it). Aggro scales the trigger ranges: personalities differ in
// WHEN they spend, which is what makes the race a contest.

/** Baseline boost wishspeed multiplier (DEFAULT_PILOT.boostMult;
 * pilotFor draws each racer's own around it). */
export const BOOST_MULT = 1.55;
/** Full tank burns in ~3s. */
const BOOST_DRAIN = 1 / 3;
/** Empty tank refills in ~12s. */
const BOOST_RECHARGE = 1 / 12;
/** M22: extra refill per second at full draft depth — a committed wake
 * rider roughly DOUBLES its regen. The economy makes trains: line up,
 * hoard, detonate. */
export const DRAFT_REFILL = 1 / 14;
/** Don't ignite below this. Sized for RHYTHM: in a tight pack a rival is
 * always in range, so pilots ignite the moment they may — a low floor
 * gave 0.6s sputters at the floor level. Half a tank commits to a
 * ~1.4s attack with ~6s of visible restraint between burns. */
const BOOST_MIN_FUEL = 0.5;
/** Attack range: a rival ahead within aggro·this many units. */
const BOOST_ATTACK = 8;
/** Defend range: a chaser behind within aggro·this many units. */
const BOOST_DEFEND = 4;

export interface BoostState {
  /** Tank level 0..1. */
  fuel: number;
  boosting: boolean;
}

export const FULL_TANK: BoostState = { fuel: 1, boosting: false };

/**
 * One fixed-step of the boost economy. `headroom` is vSafe − wishspeed
 * from the pilot's last force step (PilotDebug) — while boosting, the
 * envelope capping the boost (headroom ≈ 0) ends the burn; rival gaps
 * are rail-progress deltas (null = nobody there). Pure and
 * deterministic; drivers hold the state per racer.
 */
export function boostStep(
  st: BoostState,
  pilot: Pilot,
  dt: number,
  headroom: number | null,
  gapAhead: number | null,
  gapBehind: number | null,
  /** M22 slipstream economy: 0..~1.4 draft depth ((draft−1)/DRAFT_GAIN).
   * Riding a wake REFILLS the tank on top of the base regen — trains
   * form because they pay, then detonate at attack range. */
  draftDepth = 0,
): BoostState {
  if (st.boosting) {
    const fuel = Math.max(0, st.fuel - dt * BOOST_DRAIN / pilot.efficiency);
    const capped = headroom !== null && headroom < 0.1;
    return { fuel, boosting: fuel > 0.02 && !capped };
  }
  const fuel = Math.min(
    1,
    st.fuel + dt * (BOOST_RECHARGE * pilot.regen + DRAFT_REFILL * draftDepth),
  );
  const room = headroom !== null && headroom > 0.8;
  const attack = gapAhead !== null && gapAhead < BOOST_ATTACK * pilot.aggro;
  const defend = gapBehind !== null && gapBehind < BOOST_DEFEND * pilot.aggro;
  const overflow = fuel >= 1;
  const ignite = fuel >= BOOST_MIN_FUEL && room &&
    (attack || defend || overflow);
  return { fuel, boosting: ignite };
}

// --- rubber-banding through pilot SKILL (Melder, Game AI Pro) --------------
//
// Never thrust: a trailing racer gets no extra engine — it gets a braver
// PILOT. Corner margins widen (bravery multiplies the curvature-derived
// corner speeds) and rescue jumps commit earlier (eagerness raises the
// top-up ceiling); the leader's pilot turns conservative by the same law.
// Capped so personality still dominates the modulation: at the extremes
// corners swing ±8% and jump eagerness ±25% around the seed-drawn traits.

/** Corner-speed swing at the field's extremes (fraction of bravery). */
const RUBBER_BRAVERY = 0.16;
/** Jump-eagerness swing at the extremes (fraction of eagerness). */
const RUBBER_EAGERNESS = 0.5;

/**
 * Race-position → pilot-skill modulation. `behind` ∈ [0, 1]: 0 = leading
 * the field, 1 = dead last, 0.5 = mid-pack (neutral — a lone racer or an
 * exact midfielder keeps its base traits). Drivers smooth `behind` (rank
 * order flaps in a tight pack) and write the result onto a per-racer
 * scratch pilot each decision; `base` is never mutated.
 */
export function rubberMod(base: Pilot, behind: number, out: Pilot): Pilot {
  const x = Math.min(1, Math.max(0, behind)) - 0.5;
  Object.assign(out, base);
  out.bravery = base.bravery * (1 + RUBBER_BRAVERY * x);
  out.eagerness = base.eagerness * (1 + RUBBER_EAGERNESS * x);
  return out;
}

/** Lateral offset target at sim-time t — a slow two-tone wander (closed
 * form: deterministic under fixed stepping, no draw-order coupling, and
 * mean-reverting like an OU process without carrying state). Stays well
 * inside the corridor dead zone. */
export function pilotOffset(p: Pilot, t: number): number {
  if (p.wanderAmp === 0) return 0;
  return p.wanderAmp *
    (0.65 * Math.sin(0.31 * t + p.phase1) +
      0.35 * Math.sin(0.093 * t + p.phase2));
}

/** Cruise target on an unmodified tile (the proven M11 value — boost
 * pricing multiplies it, so jump launch speeds stay ≈ 6.1). */
const CRUISE = 3.2;
/** Hard speed ceiling (the proven envelope cap). */
const VMAX = 9;
/** Lateral grip budget for corner speeds: vc = sqrt(A_LAT·r). Sized so a
 * small curve (r = 1 world) corners at ≈ cruise — the old law's proven
 * entry speed for tight bends — while large curves earn more. */
const A_LAT = 10;
/** Crest budget: centripetal v²·κ beyond this fraction of g lifts the
 * marble off the floor — brake below launch, with margin. */
const A_CREST = 16;
/** Planning deceleration for the brake sweep (the brake itself can pull
 * harder; planning softer means braking starts earlier and reads as
 * intent, not panic). */
const A_BRAKE = 6;
/** Authority: accel per unit of speed deficit… */
const KP = 5;
/** …saturating at the static-friction budget (MBHaxe μs 1.1 · g 20). */
const A_TRACTION = 22;
/** Max brake accel (matches the old law's clamp). */
const A_BRAKE_MAX = 10;
/** Linear air control (MBHaxe _airAccel). */
const AIR_ACCEL = 5;
const L_MIN = 1.0;
const L_MAX = 4.0;
/** Reynolds corridor (M14c): no lateral correction inside the dead zone
 * (the marble may wobble — pursuit alone steers); a ramped spring
 * shepherds it back between DEAD and EDGE. The PROFILE's flat floor runs
 * ±0.30, gutters to ±0.74, walls at ±0.92. */
const CORRIDOR_DEAD = 0.3;
const CORRIDOR_EDGE = 0.55;
/** Brake-sweep window (world units) — covers VMAX → CRUISE at A_BRAKE. */
const SWEEP_DIST = 8;
/** Height above the rail floor that counts as airborne. The rail line is
 * the channel floor centerline; a rolling marble's center rides ≈ its
 * radius (0.1) above it, bumps and seams excursion within ~0.3. */
const AIR_HEIGHT = 0.45;

const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

/** Max top-up jump speed (≈0.7 world of height at g 20 — a hop, not a
 * rocket; a rescue needing more than this is a respawn, not a jump). */
const JUMP_MAX = 5.2;
/** Landing clearance: the rescue aims exactly at the landing point — the
 * catch pan reaches ~1.4 under the flight path (M11), so the pan IS the
 * margin; any positive clearance here made the pilot top-up even priced
 * launches. */
const JUMP_CLEAR = 0;

/**
 * The jump decision (M14b): approaching a flight gap too slow for the
 * priced ballistic arc, the pilot may TOP-UP its vertical speed at the
 * lip so the longer hang time still crosses — computed from the marble's
 * ACTUAL state against the landing point, committed only when the
 * resulting arc lands inside the corridor. Returns [normal, targetSpeed]
 * for PhysicsEngine.jump, or null. Deterministic and repeat-safe (the
 * top-up contract makes held calls no-ops).
 */
export function wantJump(
  rail: Rail,
  idx: number,
  pos: readonly [number, number, number],
  vel: readonly [number, number, number],
  nextRail: Rail | null = null,
  pilot: Pilot = DEFAULT_PILOT,
): [[number, number, number], number] | null {
  const t = rail.tans[idx];
  if (!t) return null;
  if (rail.mods[idx]?.flight) return null; // already committed
  if (pos[1] - rail.pts[idx][1] >= 0.3) return null; // must be rolling
  if (vel[1] > 1.0) return null; // already rising (jumped/bounced) — once
  const n = rail.tans.length;
  const modAt = (i: number): RailMod | null =>
    i < n ? rail.mods[i] : nextRail ? nextRail.mods[i - n] ?? null : null;
  const ptAt = (i: number): readonly [number, number, number] | null =>
    rail.pts[i] ?? (nextRail ? nextRail.pts[i - n] ?? null : null);
  // Decide ONLY at the lip — the last rolling station before the flight
  // run. Any earlier there is still floor under the arc and the ballistic
  // test is meaningless (a premature version of this fired 2u early and
  // helicoptered marbles off the line).
  if (!modAt(idx + 1)?.flight) return null;
  let land = idx + 2;
  while (modAt(land)?.flight) land++;
  const ld = ptAt(land);
  if (!ld) return null;
  const gravScale = modAt(idx + 1)?.gravScale ?? 1;
  const gEff = -GRAVITY_Y * gravScale;
  const gx = ld[0] - pos[0];
  const gz = ld[2] - pos[2];
  const G = Math.hypot(gx, gz);
  if (G < 0.3) return null;
  const drop = ld[1] - pos[1]; // negative: landing below
  const vh = (vel[0] * gx + vel[2] * gz) / G;
  if (vh < 0.8) return null; // can't cross at any hang time worth having
  const tF = G / vh;
  const yAt = vel[1] * tF - 0.5 * gEff * tF * tF;
  if (yAt >= drop + JUMP_CLEAR) return null; // priced flight already works
  const vyNeed = (drop + JUMP_CLEAR + 0.5 * gEff * tF * tF) / tF;
  if (vyNeed <= vel[1] || vyNeed > JUMP_MAX * pilot.eagerness) return null;
  // Slope-aware floor normal from the tangent plane (approach cells are
  // near-flat by construction, so n ≈ up and the top-up is ≈ vertical).
  let nx = -t[0] * t[1];
  let ny = 1 - t[1] * t[1];
  let nz = -t[2] * t[1];
  const nl = Math.hypot(nx, ny, nz);
  if (nl < 1e-6 || ny <= 0.5) return null;
  nx /= nl;
  ny /= nl;
  nz /= nl;
  return [[nx, ny, nz], vyNeed / ny];
}

/** Per-step introspection (debug harnesses pass one in). */
export interface PilotDebug {
  mode?: string;
  wishspeed?: number;
  vSafe?: number;
  deficit?: number;
  grounded?: boolean;
  look?: readonly [number, number, number];
}

/** A neighboring racer the pilot can see (M15 separation steering). */
export interface Neighbor {
  pos: readonly [number, number, number];
  bravery: number;
}

/**
 * The complete autopilot step at a station. Same contract as
 * rail.ts stationForce (drivers swap one call), plus a pilot and the
 * other racers on the track.
 */
export function autopilotForce(
  rail: Rail,
  idx: number,
  pos: readonly [number, number, number],
  vel: readonly [number, number, number],
  mass: number,
  nextRail: Rail | null = null,
  pilot: Pilot = DEFAULT_PILOT,
  dbg?: PilotDebug,
  others?: readonly Neighbor[],
  /** Fuel burn (boostStep): raises the target inside the same envelope. */
  boosting = false,
  /** Draft multiplier (M20a, draftFactor): raises the target inside the
   * same envelope — stacks multiplicatively with boost; vSafe still
   * caps, so a drafting marble brakes for every corner unchanged. */
  targetMult = 1,
): [number, number, number] | null {
  const t = rail.tans[idx];
  if (!t) return null;
  const mod = rail.mods[idx];
  // Flight zones keep their plan-time pricing: no propulsion, propeller
  // zones cancel part of gravity. (M14b adds the jump decision + air
  // lineup on top.)
  if (mod?.flight) {
    if (dbg) dbg.mode = "flight";
    if (mod.gravScale !== undefined && mod.gravScale !== 1) {
      return [0, (1 - mod.gravScale) * -GRAVITY_Y * mass, 0];
    }
    return null;
  }

  const n = rail.tans.length;
  const tanAt = (i: number): readonly [number, number, number] | null =>
    rail.tans[i] ?? (nextRail ? nextRail.tans[i - n] ?? null : null);
  const ptAt = (i: number): readonly [number, number, number] | null =>
    rail.pts[i] ?? (nextRail ? nextRail.pts[i - n] ?? null : null);
  const modAt = (i: number): RailMod | null =>
    i < n ? rail.mods[i] : nextRail ? nextRail.mods[i - n] ?? null : null;
  const endS = rail.s[n - 1] ?? 0;
  const sAt = (i: number): number | null =>
    i < n
      ? rail.s[i]
      : nextRail && nextRail.s[i - n] !== undefined
      ? endS + RAIL_STEP + nextRail.s[i - n]
      : null;

  // --- speed planning: online backward brake sweep over the lookahead ---
  const sHere = rail.s[idx];
  const targetHere = CRUISE * (mod?.speed ?? 1) *
    (boosting ? pilot.boostMult : 1) * targetMult;
  let vSafe = VMAX;
  let climbTy = t[1];
  let prev: readonly [number, number, number] = t;
  let prevS = sHere;
  for (let k = idx + 1;; k++) {
    const look = tanAt(k);
    const sk = sAt(k);
    if (!look || sk === null) break;
    // The sweep stops at a flight zone: its rail "curvature" is the priced
    // ballistic profile diving through the gap, not floor geometry —
    // reading it as a crest braked marbles below launch speed at jump lips
    // (they crept off the lip and wedged in the catch pan).
    if (modAt(k)?.flight) break;
    const ds = sk - prevS;
    if (ds > 1e-6) {
      const hp = Math.hypot(prev[0], prev[2]);
      const hl = Math.hypot(look[0], look[2]);
      let yaw = 0;
      if (hp > 1e-4 && hl > 1e-4) {
        const dot = clamp(
          (prev[0] * look[0] + prev[2] * look[2]) / (hp * hl),
          -1,
          1,
        );
        yaw = Math.acos(dot);
      }
      const pitchD = Math.asin(clamp(look[1], -1, 1)) -
        Math.asin(clamp(prev[1], -1, 1));
      const kYaw = yaw / ds;
      const kCrest = Math.max(0, -pitchD) / ds; // convex only — valleys free
      const targetK = CRUISE * (modAt(k)?.speed ?? 1);
      const grip = pilot.bravery;
      const vYaw = kYaw > 1e-4 ? Math.sqrt(A_LAT / kYaw) * grip : VMAX;
      const vCrest = kCrest > 1e-4 ? Math.sqrt(A_CREST / kCrest) * grip : VMAX;
      const vc = Math.min(VMAX, Math.max(targetK, Math.min(vYaw, vCrest)));
      const dist = sk - sHere;
      // brakeStyle (M20b): late-brakers PLAN a harder decel (capped so
      // the actual brake clamp A_BRAKE_MAX can always deliver it).
      const allowed = Math.sqrt(
        vc * vc + 2 * A_BRAKE * pilot.brakeStyle * dist,
      );
      if (allowed < vSafe) vSafe = allowed;
    }
    climbTy = Math.max(climbTy, look[1]);
    prev = look;
    prevS = sk;
    if (sk - sHere > SWEEP_DIST) break;
  }

  // Momentum arrival: a climb ahead raises the target — the pilot builds
  // speed BEFORE the slope instead of being carried up it.
  const climbing = climbTy > 0.12;
  const target = climbing ? targetHere * (1 + 0.5 * climbTy) : targetHere;
  const wishspeed = Math.min(target, vSafe);

  // --- pure pursuit: the lookahead point → wishdir ---
  const speed = Math.hypot(vel[0], vel[1], vel[2]);
  const L = clamp(pilot.lookGain * speed, L_MIN, L_MAX);
  let j = idx;
  for (;;) {
    const sj = sAt(j + 1);
    if (sj === null || sj - sHere > L) break;
    j++;
  }
  const look = ptAt(j) ?? rail.pts[idx];
  // Pursuit steers YAW ONLY: the wishdir is the HORIZONTAL direction to
  // the lookahead point — pitch comes from the local ground plane below
  // (grounded projection) or stays level (air). Seeking the point in 3D
  // aimed the pilot INTO the floor at crests (ramp up into immediate
  // down: the point sits below the marble) — it accelerated toward the
  // ground and jittered against the crest face.
  let wx = look[0] - pos[0];
  let wy = 0;
  let wz = look[2] - pos[2];
  if (pilot.offset !== 0) {
    const tj = tanAt(j) ?? t;
    const nl = Math.hypot(tj[2], tj[0]);
    if (nl > 1e-6) {
      wx += (tj[2] / nl) * pilot.offset;
      wz += (-tj[0] / nl) * pilot.offset;
    }
  }
  let wl = Math.hypot(wx, wz);
  if (wl < 1e-6) {
    // Degenerate (drop shafts: the point is straight below) — fall back
    // to the local tangent's heading.
    wx = t[0];
    wz = t[2];
    wl = Math.hypot(wx, wz);
    if (wl < 1e-6) return null;
  }
  wx /= wl;
  wz /= wl;

  const grounded = pos[1] - rail.pts[idx][1] < AIR_HEIGHT;
  const out: [number, number, number] = [0, 0, 0];
  if (dbg) {
    dbg.wishspeed = wishspeed;
    dbg.vSafe = vSafe;
    dbg.grounded = grounded;
    dbg.look = look;
    dbg.mode = grounded ? "coast" : "air";
  }

  if (grounded) {
    // Project wishdir onto the ground plane (normal ≈ tangent-plane up)
    // so authority pushes along the slope, not into the floor.
    let nx = -t[0] * t[1];
    let ny = 1 - t[1] * t[1];
    let nz = -t[2] * t[1];
    const nlen = Math.hypot(nx, ny, nz);
    if (nlen > 1e-6) {
      nx /= nlen;
      ny /= nlen;
      nz /= nlen;
      const d = wx * nx + wy * ny + wz * nz;
      wx -= d * nx;
      wy -= d * ny;
      wz -= d * nz;
      const l2 = Math.hypot(wx, wy, wz);
      if (l2 > 1e-6) {
        wx /= l2;
        wy /= l2;
        wz /= l2;
      } else {
        wx = t[0];
        wy = t[1];
        wz = t[2];
      }
    }
    const vAlongT = vel[0] * t[0] + vel[1] * t[1] + vel[2] * t[2];
    if (vAlongT > vSafe) {
      if (dbg) dbg.mode = "brake";
      // Decisive brake above the planned envelope — its own clamp, like
      // MBHaxe braking vs rolling authority.
      const a = Math.min(A_BRAKE_MAX, 3 + (vAlongT - vSafe) * 2);
      out[0] -= t[0] * a * mass;
      out[1] -= t[1] * a * mass;
      out[2] -= t[2] * a * mass;
    } else {
      const vAlongW = vel[0] * wx + vel[1] * wy + vel[2] * wz;
      const deficit = wishspeed - vAlongW;
      if (dbg) dbg.deficit = deficit;
      if (deficit > 0) {
        if (dbg) dbg.mode = "accel";
        // Icy: grip scales BOTH responsiveness and the ceiling — on ice
        // the pilot wants the same speed and simply cannot have it.
        const grip = mod?.icy ? 0.3 : 1;
        const a = Math.min(KP * grip * deficit, A_TRACTION * grip);
        out[0] += wx * a * mass;
        out[1] += wy * a * mass;
        out[2] += wz * a * mass;
      }
    }
  } else {
    // Airborne: weak linear control toward the lookahead, horizontal only
    // (lift is gravity's business).
    const hl = Math.hypot(wx, wz);
    if (hl > 1e-4) {
      out[0] += (wx / hl) * AIR_ACCEL * mass;
      out[2] += (wz / hl) * AIR_ACCEL * mass;
    }
  }

  // Separation steering (M15): a neighbor occupying the path just ahead
  // gets a lateral yield — UNLESS this pilot is at least as brave, in
  // which case the contact happens (shoving is content). Behind-me
  // neighbors are ignored; the front marble never yields to a chaser.
  if (others && grounded) {
    const nl3 = Math.hypot(t[2], t[0]);
    if (nl3 > 1e-6) {
      const ux = t[2] / nl3;
      const uz = -t[0] / nl3;
      for (const o of others) {
        const dx = o.pos[0] - pos[0];
        const dy = o.pos[1] - pos[1];
        const dz = o.pos[2] - pos[2];
        const dist = Math.hypot(dx, dy, dz);
        if (dist > 0.5 || dist < 1e-6) continue;
        if (dx * vel[0] + dz * vel[2] <= 0) continue; // behind me
        if (pilot.bravery >= o.bravery) continue; // shove through
        const side = dx * ux + dz * uz;
        const away = side >= 0 ? -1 : 1;
        const a = 6 * (1 - dist / 0.5);
        out[0] += ux * away * a * mass;
        out[2] += uz * away * a * mass;
      }
    }
  }
  // Reynolds corridor dead zone (M14c): outside centering zones, a
  // marble within ±CORRIDOR_DEAD of its personal line (rail + wander
  // offset) gets NO lateral correction — the wobble is what reads as
  // alive. Toward the corridor edge, a ramped spring-damper shepherds it
  // back before the wall does.
  const centering = (mod?.speed !== undefined && mod.speed > 1) ||
    mod?.center === true;
  if (grounded && !centering) {
    const r = rail.pts[idx];
    const nl2 = Math.hypot(t[2], t[0]);
    if (nl2 > 1e-6) {
      const ux = t[2] / nl2;
      const uz = -t[0] / nl2;
      const off = (pos[0] - r[0]) * ux + (pos[2] - r[2]) * uz - pilot.offset;
      const mag = Math.abs(off);
      if (mag > CORRIDOR_DEAD) {
        const ramp = Math.min(
          1,
          (mag - CORRIDOR_DEAD) / (CORRIDOR_EDGE - CORRIDOR_DEAD),
        );
        const lvel = vel[0] * ux + vel[2] * uz;
        const a = clamp(
          (-28 * (off - Math.sign(off) * CORRIDOR_DEAD) - 8 * lvel) * ramp,
          -10,
          10,
        );
        out[0] += ux * a * mass;
        out[2] += uz * a * mass;
      }
    }
  }
  // --- shared zone extras (identical to the assist law) ---
  // Lateral centering spring in boost/split/gravity zones: launches and
  // merges must be straight; these zones are straight by construction.
  if (centering) {
    const r = rail.pts[idx];
    const nl = Math.hypot(t[2], t[0]) || 1;
    const ux = t[2] / nl;
    const uz = -t[0] / nl;
    const off = (pos[0] - r[0]) * ux + (pos[2] - r[2]) * uz;
    const lvel = vel[0] * ux + vel[2] * uz;
    const a = clamp(-40 * off - 12 * lvel, -14, 14);
    out[0] += ux * a * mass;
    out[2] += uz * a * mass;
  }
  // Chutes pull harder — descending stations only (grav mods never fight
  // a climb; that wedged marbles at ramp bases).
  if (mod?.grav && mod.grav !== 1 && t[1] < -0.05 && climbTy < 0.12) {
    out[1] += (mod.grav - 1) * GRAVITY_Y * mass;
  }
  return out[0] === 0 && out[1] === 0 && out[2] === 0 ? null : out;
}
